import { useState, useEffect } from 'react';
import { useFetcher, useParams } from 'react-router-dom';
import WarningIcon from '@mui/icons-material/Warning';
import {
  Button,
  Stack,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Typography,
  Autocomplete,
  ListItemText,
} from '@mui/material';
import { useSuspenseQuery } from '@tanstack/react-query';
import { createColumnHelper, ColumnDef } from '@tanstack/react-table';
import { MemberRepository } from 'src/api/member_repository';
import { FeedbackPopup, FeedbackType } from 'src/components/feedback_popup';
import { DataTable } from 'src/components/table';
import { getPlatform, getRegion } from 'src/config/environment';
import { useUser } from 'src/hooks/use_auth';
import { useMembers } from 'src/hooks/use_members';
import { RoleFieldsFragment, UserFieldsFragment } from 'src/types';
import { DistributionPlatform } from 'src/types/env';
import { MemberAction } from 'src/types/loaders';
import { UserRole } from 'src/types/user_roles';
import {
  notifySuccess,
  notifyError,
  getRoleDisplayName,
  getRoleDescription,
} from 'src/utils';
import { getEnumValue } from 'src/utils/enum_utils';

// Column identifiers for members table
enum MemberColumns {
  Member = 'MEMBER_EMAIL',
  Role = 'MEMBER_ROLE',
  Action = 'MEMBER_ACTIONS',
}

// Create column helper for typed columns
const columnHelper = createColumnHelper<UserFieldsFragment>();

export type MemberListProps = {
  memberRepository: MemberRepository;
  isBackgroundMode?: boolean;
};

export const MemberList: React.FC<MemberListProps> = ({
  memberRepository,
  isBackgroundMode = false,
}) => {
  const { members, totalCount, page, perPage, handlePageChange } = useMembers(
    memberRepository,
    !isBackgroundMode, // Pass useLoader=true for normal mode, false for background mode
  );
  const userInfo = useUser();
  const { organization } = useParams();
  const fetcher = useFetcher();
  const canSelectMultipleRoles = getPlatform() === DistributionPlatform.Samd;

  // State for Assign Roles dialog
  const [assignRolesDialog, setAssignRolesDialog] = useState({
    open: false,
    memberId: '',
    memberEmail: '',
    currentRoles: [] as RoleFieldsFragment[],
    selectedRoles: [] as RoleFieldsFragment[],
    error: '',
  });

  // State for Remove Member dialog
  const [removeMemberDialog, setRemoveMemberDialog] = useState({
    open: false,
    memberId: '',
    memberEmail: '',
  });

  const { data: availableRoles } = useSuspenseQuery<RoleFieldsFragment[]>({
    queryKey: ['assignableRoles'],
    queryFn: async () => {
      return memberRepository.getAssignableRoles();
    },
    // Prevent refetching when in background mode
    refetchOnMount: !isBackgroundMode,
    refetchOnWindowFocus: !isBackgroundMode,
  });

  const handleAssignRoles = (memberId: string) => {
    const member = members.find((m) => m.userId === memberId);
    if (member) {
      const currentRoles = member.roles || [];
      setAssignRolesDialog({
        open: true,
        memberId,
        memberEmail: member.email,
        currentRoles,
        selectedRoles: [...currentRoles],
        error: '',
      });
    }
  };

  const handleRemoveMember = (memberId: string) => {
    const member = members.find((m) => m.userId === memberId);
    if (member) {
      setRemoveMemberDialog({
        open: true,
        memberId,
        memberEmail: member.email,
      });
    }
  };

  const handleCloseAssignRolesDialog = () => {
    setAssignRolesDialog({
      open: false,
      memberId: '',
      memberEmail: '',
      currentRoles: [],
      selectedRoles: [],
      error: '',
    });
  };

  const handleCloseRemoveMemberDialog = () => {
    setRemoveMemberDialog({
      open: false,
      memberId: '',
      memberEmail: '',
    });
  };

  const handleRoleChange = (
    roles: RoleFieldsFragment | RoleFieldsFragment[] | null,
  ) => {
    if (canSelectMultipleRoles) {
      setAssignRolesDialog((prev) => ({
        ...prev,
        selectedRoles: roles as RoleFieldsFragment[],
        error:
          (roles as RoleFieldsFragment[]).length === 0
            ? 'At least one role is required'
            : '',
      }));
    } else {
      setAssignRolesDialog((prev) => ({
        ...prev,
        selectedRoles: [roles as RoleFieldsFragment],
        error: !roles ? 'At least one role is required' : '',
      }));
    }
  };

  const handleConfirmAssignRoles = async () => {
    if (assignRolesDialog.selectedRoles.length === 0) {
      setAssignRolesDialog((prev) => ({
        ...prev,
        error: 'At least one role is required',
      }));
      return;
    }

    const formData = new FormData();
    formData.append('actionType', MemberAction.ASSIGN_ROLE);
    formData.append('memberId', assignRolesDialog.memberId);

    // Append each role ID separately
    assignRolesDialog.selectedRoles.forEach((role) => {
      formData.append('roles', role.id);
    });

    await fetcher.submit(formData, {
      method: 'POST',
      action: `/${organization}/members`,
    });

    handleCloseAssignRolesDialog();
  };

  const handleConfirmRemoveMember = async () => {
    const formData = new FormData();
    formData.append('actionType', MemberAction.REMOVE_USER);
    formData.append('memberId', removeMemberDialog.memberId);

    await fetcher.submit(formData, {
      method: 'POST',
      action: `/${organization}/members`,
    });

    handleCloseRemoveMemberDialog();
  };

  // Handle action results
  useEffect(() => {
    if (fetcher.data && fetcher.state === 'idle') {
      const actionResult = fetcher.data;

      if (actionResult.action === MemberAction.ASSIGN_ROLE) {
        if (actionResult.success) {
          notifySuccess('Roles assigned successfully.');
        } else {
          notifyError('Failed to assign roles. Please try again later.');
        }
      } else if (actionResult.action === MemberAction.REMOVE_USER) {
        if (actionResult.success) {
          notifySuccess('Member removed successfully.');
        } else {
          notifyError('Failed to remove member. Please try again later.');
        }
      }
    }
  }, [fetcher.data, fetcher.state]);

  // Helper functions to get columns and headers
  const getMemberColumns = (): ColumnDef<UserFieldsFragment, string>[] => [
    columnHelper.accessor('email', {
      id: MemberColumns.Member,
      header: 'Member',
      minSize: 300,
      cell: (info) => info.getValue() || 'N/A',
    }),
    columnHelper.display({
      id: MemberColumns.Role,
      header: 'Role',
      minSize: 120,
      size: 150,
      cell: (props) => {
        const member = props.row.original;
        const firstRole = member.roles.filter(
          (role) => role.name != 'post-rp-enabled',
        )[0];

        if (!firstRole) {
          return 'No Role';
        }

        const userRole = getEnumValue(UserRole, firstRole.name);

        return getRoleDisplayName(userRole, getPlatform(), getRegion());
      },
    }),
    columnHelper.display({
      id: MemberColumns.Action,
      header: 'Action',
      minSize: 220,
      maxSize: 220,
      cell: (props) => {
        const member = props.row.original;
        const isCurrentUser = userInfo?.email === member.email;

        if (isCurrentUser) {
          return '';
        }

        return (
          <Stack
            direction='row'
            sx={{
              justifyContent: 'flex-start',
              ml: -1,
            }}
          >
            <Button
              variant='text'
              color='primary'
              onClick={() => handleAssignRoles(member.userId)}
            >
              ASSIGN ROLES
            </Button>
            <Divider
              orientation='vertical'
              sx={{ height: '14px', alignSelf: 'center' }}
            />
            <Button
              variant='text'
              color='primary'
              onClick={() => handleRemoveMember(member.userId)}
            >
              REMOVE
            </Button>
          </Stack>
        );
      },
    }),
  ];

  return (
    <>
      <DataTable
        data={members}
        columns={getMemberColumns()}
        paginationProps={{
          pageSize: perPage,
          currentPage: page,
          itemCount: totalCount,
          onPageSelected: handlePageChange,
        }}
        ariaLabel='members table'
      />
      <Dialog
        open={assignRolesDialog.open}
        onClose={handleCloseAssignRolesDialog}
        maxWidth='sm'
        fullWidth
      >
        <DialogTitle>
          Assign roles to member
          <Typography
            variant='body2'
            component='div'
            sx={{
              color: 'text.secondary',
            }}
          >
            Select the roles you want to assign to{' '}
            {assignRolesDialog.memberEmail}
          </Typography>
        </DialogTitle>

        <DialogContent>
          <Autocomplete
            multiple={canSelectMultipleRoles}
            limitTags={1}
            options={availableRoles}
            value={
              canSelectMultipleRoles
                ? assignRolesDialog.selectedRoles
                : assignRolesDialog.selectedRoles.length > 0
                  ? assignRolesDialog.selectedRoles[0]
                  : null
            }
            onChange={(_, newRoles) => handleRoleChange(newRoles)}
            isOptionEqualToValue={(option, value) => option.id === value.id}
            filterSelectedOptions
            sx={{ mt: 2 }}
            renderInput={(params) => (
              <TextField
                {...params}
                label='Role(s)'
                placeholder={
                  assignRolesDialog.selectedRoles.length === 0
                    ? 'Select roles'
                    : ''
                }
                error={!!assignRolesDialog.error}
                helperText={assignRolesDialog.error || ' '}
              />
            )}
            renderOption={(props, option: RoleFieldsFragment) => {
              const { key: _key, ...otherProps } = props;
              const userRole = getEnumValue(UserRole, option.name);
              return (
                <li {...otherProps} key={option.id}>
                  <ListItemText
                    primary={getRoleDisplayName(
                      userRole,
                      getPlatform(),
                      getRegion(),
                    )}
                    secondary={getRoleDescription(userRole, getPlatform())}
                  />
                </li>
              );
            }}
            getOptionLabel={(option: RoleFieldsFragment) =>
              getRoleDisplayName(
                getEnumValue(UserRole, option.name),
                getPlatform(),
                getRegion(),
              )
            }
            getOptionKey={(option: RoleFieldsFragment) => option.id}
          />
        </DialogContent>

        <DialogActions>
          <Button onClick={handleCloseAssignRolesDialog} size='large'>
            CANCEL
          </Button>
          <Button
            onClick={handleConfirmAssignRoles}
            variant='contained'
            size='large'
            disabled={assignRolesDialog.selectedRoles.length === 0}
          >
            ASSIGN ROLES
          </Button>
        </DialogActions>
      </Dialog>
      <FeedbackPopup
        data={{
          primaryButtonText: 'REMOVE MEMBER',
          secondaryButtonText: 'NO',
          title: 'Remove Member',
          subtitle: `Are you sure you want to remove ${removeMemberDialog.memberEmail} from the organization?`,
          icon: WarningIcon,
          type: FeedbackType.Error,
        }}
        isDialogOpen={removeMemberDialog.open}
        onPrimaryButtonClick={handleConfirmRemoveMember}
        onSecondaryButtonClick={handleCloseRemoveMemberDialog}
        handleDialogClose={handleCloseRemoveMemberDialog}
      />
    </>
  );
};
